import {useEffect, useState, useMemo, useRef, useCallback} from "react";
import {Values} from "@reside/forms";
import {Draft} from "../../admission-explorer/model";
import {FileService} from "../../../services/FileService";
import {logErrorToSentry} from "../../../utils/sentry";

const FILE_ID_PATTERN = /\/api\/files\/([a-f0-9-]+)/i;
const S3_URL_PREFIX = "https://s3.";

// Only process these specific answer keys for pre-signed URLs
// Add more keys here as needed (e.g., "otherFileField")
const KEYS_TO_PROCESS = new Set([
  "facilityHandbook",
  "facilityRights",
  "facilityContactInformationForm",
  "preflight_secondaryCommercialIdCard",
  "facilityCorporateHotline",
  "udalsaDocumentLink",
  "OtherProvidersDocumentLink", // First letter is intentional upercase to match the template key name.
  "residentGuideDocumentLink",
]);

export type ProcessedFileAnswers = Readonly<{
  /** Draft whose whitelisted file answers carry a (display-only) pre-signed URL. */
  draft: Draft | undefined;
  /**
   * Replace any pre-signed S3 URL we injected for display with the original
   * durable ``/api/files/<uuid>`` value before answers are persisted. The
   * pre-signed URL is short-lived (~10 min); persisting it corrupts the
   * admission (DEV-5539). Keep it on screen, never in the saved answers.
   */
  restorePersistedFileAnswers: <T extends Record<string, unknown>>(
    answers: T,
  ) => T;
}>;

/**
 * Hook that resolves specific file answer values to pre-signed S3 URLs **for
 * display only**.
 *
 * Only processes answer keys listed in KEYS_TO_PROCESS to avoid interfering
 * with other file handling logic (e.g., signatures which have their own flow).
 * The original durable value is remembered so it can be restored before any
 * save via ``restorePersistedFileAnswers``.
 *
 * @param draft - Draft object containing admission answers
 * @returns the draft with display-resolved file URLs plus a restore helper
 */
export const useProcessedFileAnswers = (
  draft: Draft | undefined,
): ProcessedFileAnswers => {
  const [processedAnswers, setProcessedAnswers] = useState<Values>({});
  const processedKeysRef = useRef<Set<string>>(new Set());
  const inFlightRequestsRef = useRef<Set<string>>(new Set());
  // pre-signed URL we injected -> the durable value it replaced. Keyed by the
  // URL (not the answer key) so it can't go stale or collide across re-uploads
  // or admission switches: a pre-signed URL is unique to one durable value.
  const durableByPresignedUrlRef = useRef<Record<string, string>>({});

  useEffect(() => {
    if (!draft?.answers) {
      setProcessedAnswers({});
      processedKeysRef.current.clear();
      inFlightRequestsRef.current.clear();
      durableByPresignedUrlRef.current = {};
      return;
    }

    const processFileAnswers = async () => {
      const answers = draft.answers;
      const updates: Record<string, string> = {};

      for (const [key, value] of Object.entries(answers)) {
        // Only process whitelisted keys
        if (!KEYS_TO_PROCESS.has(key)) continue;

        if (typeof value !== "string") continue;

        const urlMatch = FILE_ID_PATTERN.exec(value);
        if (!urlMatch) continue;

        // Skip if already a pre-signed S3 URL
        if (value.startsWith(S3_URL_PREFIX)) continue;

        const fileId = urlMatch[1];
        const cacheKey = `${key}:${fileId}`;

        // Skip if already processed or currently being fetched
        if (
          processedKeysRef.current.has(cacheKey) ||
          inFlightRequestsRef.current.has(cacheKey)
        ) {
          continue;
        }

        // Mark as in-flight to prevent duplicate requests
        inFlightRequestsRef.current.add(cacheKey);

        try {
          const fileResponse = await FileService.getFileReference(fileId);

          if (fileResponse.ok && fileResponse.data?.url) {
            updates[key] = fileResponse.data.url;
            // Map this pre-signed URL back to the durable value it replaced,
            // so it is restored exactly before saving.
            durableByPresignedUrlRef.current[fileResponse.data.url] = value;
            processedKeysRef.current.add(cacheKey);
          }
        } catch {
          // Silently handle errors - keep original URL
          logErrorToSentry(
            new Error(`Failed to get pre-signed URL for file ${fileId}`),
            {fileId, cacheKey},
          );
        } finally {
          // Remove from in-flight after completion (success or error)
          inFlightRequestsRef.current.delete(cacheKey);
        }
      }

      if (Object.keys(updates).length > 0) {
        setProcessedAnswers(prev => ({...prev, ...updates}));
      }
    };

    processFileAnswers();
  }, [draft?.answers]);

  const draftWithProcessedAnswers = useMemo(() => {
    if (!draft) return draft;

    // Merge processed answers with original answers
    const mergedAnswers = {
      ...draft.answers,
      ...processedAnswers,
    };

    return {
      ...draft,
      answers: mergedAnswers,
    };
  }, [draft, processedAnswers]);

  const restorePersistedFileAnswers = useCallback(
    <T extends Record<string, unknown>>(answers: T): T => {
      if (!answers) return answers;
      const durableByUrl = durableByPresignedUrlRef.current;
      let result: T | null = null;
      for (const key of Object.keys(answers)) {
        const value = answers[key];
        if (typeof value === "string" && durableByUrl[value] !== undefined) {
          if (!result) result = {...answers};
          (result as Record<string, unknown>)[key] = durableByUrl[value];
        }
      }
      return result ?? answers;
    },
    [],
  );

  return {draft: draftWithProcessedAnswers, restorePersistedFileAnswers};
};
